'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import { format as formatDate } from 'date-fns';
import {
  PulseButton,
  Typography,
  Dialog,
  Select,
  Option,
  CircularProgress,
} from 'src/components/PulseUI';

import { useGetDataIssueDetailsQuery as useGetDataIssueDetails, useResolveDataIssueMutation as useResolveDataIssue } from 'src/api/redux';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';

import { DataIssueTypeSelector } from './DataIssueTypeSelector';
import { DataIssueAddressSuggestions } from './DataIssueAddressSuggestions';
import { DataIssueEmailSuggestions } from './DataIssueEmailSuggestions';
import { DataIssueComments } from './DataIssueComments';
import { DataIssuePhone } from './DataIssuePhone';
import { TypeIcon } from 'src/components';
import { AddressType, DataIssueTypes } from './types';
import Link from 'next/link';
import { getProfileUrl } from 'src/utils/profileUrl';
import { faExternalLinkAlt } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';

interface ResolveDataIssueDialogProps {
  open: boolean;
  onClose: () => void;
  dataIssueId: string;
}

const ResolveDataIssueDialog = ({
  open,
  onClose,
  dataIssueId,
}: ResolveDataIssueDialogProps) => {
  const log = useLogging();
  const { setNotification } = useNotification();
  const [resolveDataIssue, { isLoading: isSubmitLoading }] =
    useResolveDataIssue();

  const { data: dataIssue, isLoading } = useGetDataIssueDetails(
    { dataIssueId },
    { skip: !dataIssueId }
  );

  const issueType = dataIssue?.issue_type?.toLowerCase();
  const dataIssueAddressKeyFromProps = dataIssue?.new_data?.address_key;

  const [email, setEmail] = useState('');
  const [initialEmailId, setInitialEmailId] = useState<string | null>(null);
  const [phone, setPhone] = useState('');
  const [addressData, setAddressData] = useState<AddressType | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const handleSetAddressData = useCallback((value: any) => {
    setAddressData(value);
  }, []);

  useEffect(() => {
    if (dataIssue) {
      setStatus(dataIssue.status);
      const initialEmailValue = dataIssue?.new_data?.email || '';
      setEmail(initialEmailValue);
      setInitialEmailId(initialEmailValue || null);
      setPhone(dataIssue?.new_data?.phone || '');
      setAddressData((dataIssue?.new_data as AddressType) || null);
    } else {
      setStatus(null);
      setEmail('');
      setInitialEmailId(null);
      setPhone('');
      setAddressData(null);
    }
  }, [dataIssue]);

  const handleOnClose = () => {
    setStatus(null);
    setEmail('');
    setInitialEmailId(null);
    setPhone('');
    setAddressData(null);
    onClose();
  };

  const handleSubmit = async () => {
    const dataToSubmit = {
      phone: phone ? { phone } : null,
      address: addressData ? addressData : null,
      email: email ? { email } : null,
    };

    try {
      await resolveDataIssue({
        dataIssueId,
        newData: dataToSubmit[issueType],
        status,
      }).unwrap();

      handleOnClose();

      setNotification({
        title: 'Data Issue Resolved',
        message: 'The data issue has been updated.',
        type: 'success',
      });
    } catch (err) {
      setNotification({
        title: 'Error',
        message:
          err?.error_message ||
          'An error occurred while updating data issue. Our team has been notified.',
        type: 'error',
      });
      log.exception(err);
    }
  };

  const isSubmitDisabled = useMemo(() => {
    if (isSubmitLoading) return true;

    if (issueType === 'address' && !addressData) return true;
    if (issueType === 'email' && !email) return true;
    if (issueType === 'phone' && !phone) return true;

    return false;
  }, [isSubmitLoading, issueType, addressData, email, phone]);

  return (
    <Dialog
      isOpen={open}
      onClose={handleOnClose}
      className="h-auto w-full max-w-4xl"
      aria-labelledby="resolve-data-issue-dialog-title"
      data-testid="resolve-data-issue-dialog"
    >
      <Dialog.Header>
        <span id="resolve-data-issue-dialog-title">
          <Typography variant="h4" bold>
            Data Issue Report
          </Typography>
        </span>
      </Dialog.Header>
      <Dialog.Content
        className="p-4 space-y-4"
        data-testid="resolve-data-issue-content"
      >
        {isLoading ? (
          <div
            className="flex items-center justify-center py-12"
            data-testid="resolve-data-issue-loading"
          >
            <CircularProgress aria-label="Loading data issue details" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-4 gap-4">
              <div className="flex items-center gap-2">
                <TypeIcon
                  type={dataIssue?.content_type}
                  className="text-sky-700"
                />
                <div className="overflow-hidden">
                  <Link
                    href={getProfileUrl(dataIssue?.content_type || 'hcp', dataIssue?.provider_id)}
                    className="text-sm font-medium text-link hover:underline truncate flex items-center gap-1"
                    data-testid="provider-profile-link"
                    aria-label={`View profile for ${
                      dataIssue?.name || 'provider'
                    }`}
                  >
                    {dataIssue?.name || 'n/a'}
                    <Icon
                      icon={faExternalLinkAlt}
                      className="text-xs"
                      aria-hidden="true"
                    />
                  </Link>
                  <div className="text-xs text-tertiary truncate">
                    {dataIssue?.city}, {dataIssue?.state}
                  </div>
                </div>
              </div>
              <div className="flex flex-col space-y-1">
                <Typography variant="subtitle2" bold>
                  Status
                </Typography>
                <Select
                  size="sm"
                  value={status}
                  placeholder="Status"
                  onChange={(e, value) => setStatus(value as string | null)}
                  aria-label="Data issue status"
                  data-testid="status-select"
                  className="rounded-full"
                >
                  <Option value="APPROVED" data-testid="status-option-approved">
                    <span className="flex items-center gap-2">
                      <span className="text-green-500">●</span>
                      Approved
                    </span>
                  </Option>
                  <Option value="REJECTED" data-testid="status-option-rejected">
                    <span className="flex items-center gap-2">
                      <span className="text-red-500">●</span>
                      Rejected
                    </span>
                  </Option>
                  <Option
                    value="UNRESOLVED"
                    data-testid="status-option-unresolved"
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-yellow-500">●</span>
                      Unresolved
                    </span>
                  </Option>
                  <Option
                    value="REQUIRES_DATA_TEAM_REVIEW"
                    data-testid="status-option-requires-review"
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-yellow-500">●</span>
                      Resolved with Followup
                    </span>
                  </Option>
                  <Option
                    value="RESOLVED"
                    disabled={true}
                    data-testid="status-option-resolved"
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-green-500">●</span>
                      Resolved
                    </span>
                  </Option>
                  <Option
                    value="RESOLVED_WITH_FOLLOWUP"
                    disabled={true}
                    data-testid="status-option-resolved-followup"
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-green-500">●</span>
                      Resolved with Followup
                    </span>
                  </Option>
                </Select>
              </div>
              <div className="flex flex-col space-y-1">
                <Typography variant="subtitle2" bold>
                  Reported At
                </Typography>
                <Typography variant="body2" truncate>
                  {dataIssue?.reported_at
                    ? formatDate(new Date(dataIssue?.reported_at), 'PPp')
                    : '--'}
                </Typography>
              </div>
              <div className="flex flex-col space-y-1">
                <Typography variant="subtitle2" bold>
                  Reported By
                </Typography>
                <Typography variant="body2" truncate className="w-full">
                  {dataIssue?.reporting_user_email}
                </Typography>
              </div>
            </div>

            <DataIssueTypeSelector issueType={issueType as DataIssueTypes} />
            {dataIssue?.content_type && issueType === 'address' && (
              <DataIssueAddressSuggestions
                providerId={dataIssue?.provider_id}
                dataIssueAddressKey={dataIssueAddressKeyFromProps}
                setAddressData={handleSetAddressData}
                initialAddressData={(dataIssue?.new_data as AddressType) || null}
              />
            )}
            {dataIssue?.content_type && issueType === 'email' && (
              <DataIssueEmailSuggestions
                issueType={issueType}
                providerId={dataIssue?.provider_id}
                providerType={dataIssue?.content_type}
                setEmail={setEmail}
                email={email}
                emailId={initialEmailId}
                setEmailId={setInitialEmailId}
              />
            )}
            {issueType && issueType === 'phone' && (
              <DataIssuePhone phone={phone} setPhone={setPhone} />
            )}
            {dataIssue && <DataIssueComments comments={dataIssue?.comments} />}
          </>
        )}
      </Dialog.Content>
      <Dialog.Actions>
        <PulseButton
          color="danger"
          variant="outline"
          onClick={handleOnClose}
          data-testid="resolve-data-issue-cancel-button"
        >
          Cancel
        </PulseButton>
        <PulseButton
          variant="solid"
          color="primary"
          onClick={handleSubmit}
          loading={isSubmitLoading}
          disabled={isSubmitDisabled}
          data-testid="resolve-data-issue-submit-button"
        >
          Submit
        </PulseButton>
      </Dialog.Actions>
    </Dialog>
  );
};

export default ResolveDataIssueDialog;
